import type { JSX } from 'react';

import { AlertsDirectedConfig } from 'features/notification-config';
import { ExternalLink, HelpCircle } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';

import ControlPanelSection from './ControlPanelSection';

const NOTIFICATION_DOCS_URL = 'https://lidz-ai.monday.com/docs/18404070509';
const NOTIFICATION_CONFIG_TOOLTIP =
  'Esta sección edita la business rule NOTIFICATION_CONFIG_PROJECT. En el flujo legacy de lidz_front equivale ' +
  'a la configuración de notificaciones por proyecto: define qué roles reciben alertas por dominio, qué usuarios ' +
  'adicionales participan, el score mínimo para notificar y si se considera la asignación del ejecutivo.';

interface ConfigurationSectionProps {
  projectId: number;
  businessRuleDisplayName: string;
  subProjects?: { id: number; name: string }[];
}

const ConfigurationSection = ({
  projectId,
  businessRuleDisplayName,
  subProjects = [],
}: ConfigurationSectionProps): JSX.Element => (
  <div className="flex flex-col gap-8">
    <div className="h-px w-full bg-border-subtle" role="separator" aria-hidden />

    <section aria-labelledby="in-chat-config-heading" className="flex flex-col gap-4">
      <div className="flex flex-col gap-1">
        <p className="m-0 text-xs font-bold uppercase tracking-widest text-text-subtle font-body">
          1. Control operativo
        </p>
        <h2 id="in-chat-config-heading" className="m-0 text-base font-bold text-text-default font-display">
          Notificaciones in-chat
        </h2>
        <p className="m-0 max-w-3xl text-sm text-text-muted font-body">
          Activa el aviso al ejecutivo cuando el asistente detecta una acción que requiere atención humana.
        </p>
      </div>
      <ControlPanelSection
        masterProjectIdForBusinessRules={projectId}
        businessRuleDisplayName={businessRuleDisplayName}
      />
    </section>

    <div className="h-px w-full bg-border-subtle" role="separator" aria-hidden />

    <section aria-labelledby="directed-alerts-heading" className="flex flex-col gap-4">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-2">
          <div className="flex flex-col gap-1">
            <p className="m-0 text-xs font-bold uppercase tracking-widest text-text-subtle font-body">
              2. Destinatarios y alcance
            </p>
            <div className="flex items-center gap-2">
              <h2 id="directed-alerts-heading" className="m-0 text-base font-bold text-text-default font-display">
                Alertas dirigidas a usuarios
              </h2>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex size-6 shrink-0 items-center justify-center rounded-md text-text-muted
                    transition-colors hover:bg-bg-surface-raised hover:text-brand-navy focus-visible:outline-none
                    focus-visible:ring-3 focus-visible:ring-ring/50"
                    aria-label="Más información sobre NOTIFICATION_CONFIG_PROJECT"
                  >
                    <HelpCircle className="size-4" aria-hidden />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="right" className="max-w-sm text-left leading-relaxed">
                  {NOTIFICATION_CONFIG_TOOLTIP}
                </TooltipContent>
              </Tooltip>
            </div>
          </div>
        </div>
        <Button variant="outline" size="sm" asChild className="w-fit gap-2">
          <a href={NOTIFICATION_DOCS_URL} target="_blank" rel="noopener noreferrer">
            <ExternalLink className="size-4 shrink-0" aria-hidden />
            Documentación
          </a>
        </Button>
      </div>
      <AlertsDirectedConfig
        projectId={projectId}
        businessRuleDisplayName={businessRuleDisplayName}
        subProjects={subProjects}
      />
    </section>
  </div>
);

export default ConfigurationSection;
